/**
 * Derive an AI-chat conversation title from the user's first message.
 *
 * #1872 (2026-08-12): auto-titling already shipped, but as a raw character
 * cut — `text.slice(0, 60)` — with no trim, no newline/markdown cleanup, and
 * no word boundary. A conversation opened with "can you help me understand
 * why the phishing campaign funnel numbers don't match the detail page" got
 * titled "can you help me understand why the phishing campaign funnel numb"
 * — a mid-word fragment, not a title.
 *
 * This is a frontend-only cleanup (trim, collapse whitespace/newlines, strip
 * light markdown, cut on a word boundary with an ellipsis) rather than a
 * server-side LLM titling call: the AI-chat backend module
 * (apps/api/src/modules/ai-chat/**) is outside this change's file-ownership
 * boundary, so adding a new LLM-titling endpoint was not an option here. The
 * existing manual Rename UI (PATCH /ai-chat/conversations/:id) remains the
 * path to a fully custom title.
 */

const MARKDOWN_STRIP = /[`*_#>~]/g
const CODE_FENCE = /```[\s\S]*?```/g
const INLINE_CODE = /`[^`]*`/g

export const DEFAULT_CONVERSATION_TITLE = "New Conversation"

/**
 * Clean and truncate raw user input into a conversation title.
 *
 * - Strips code fences/inline code and light markdown punctuation.
 * - Collapses newlines and repeated whitespace into single spaces.
 * - Trims.
 * - If the result exceeds `maxLength`, cuts at the last word boundary before
 *   the limit (never mid-word) and appends an ellipsis. Falls back to a hard
 *   cut only when no word boundary exists in a reasonable range (e.g. one
 *   very long unbroken token), so a title is never left empty.
 * - Empty/whitespace-only input returns `DEFAULT_CONVERSATION_TITLE`.
 */
export function deriveConversationTitle(
  raw: string,
  maxLength = 60,
): string {
  const withoutCode = (raw ?? "")
    .replace(CODE_FENCE, " ")
    .replace(INLINE_CODE, " ")
  const cleaned = withoutCode
    .replace(MARKDOWN_STRIP, "")
    .replace(/\s+/g, " ")
    .trim()

  if (!cleaned) return DEFAULT_CONVERSATION_TITLE
  if (cleaned.length <= maxLength) return cleaned

  const truncated = cleaned.slice(0, maxLength)
  const lastSpace = truncated.lastIndexOf(" ")
  // Only cut on the word boundary when it leaves a meaningful chunk of text —
  // otherwise (e.g. one long unbroken word) fall back to a hard cut so the
  // title isn't reduced to a couple of characters.
  const cut =
    lastSpace > maxLength * 0.4 ? truncated.slice(0, lastSpace) : truncated
  return `${cut.trimEnd()}…`
}
